import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiSend, FiLoader, FiClipboard } = FiIcons;

const ClipboardInput = ({ onAddClipboardItem, isConnected }) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Auto-resize textarea
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [content]);

  useEffect(() => {
    // Keyboard shortcut: Ctrl+Enter to submit
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    };

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('keydown', handleKeyDown);
      return () => textarea.removeEventListener('keydown', handleKeyDown);
    }
  }, [content, isConnected]);

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Please enter some content to share');
      return;
    }

    if (!isConnected) {
      toast.error('Not connected to room');
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await onAddClipboardItem(content.trim());
      if (success) {
        setContent('');
        toast.success('Content added to shared clipboard');
      }
    } catch (error) {
      console.error('Error adding clipboard item:', error);
      toast.error('Failed to add content');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (clipboardText) {
        setContent(clipboardText);
        toast.success('Pasted from clipboard');
      } else {
        toast.error('Clipboard is empty');
      }
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      toast.error('Failed to access clipboard');
    }
  };

  return (
    <motion.div
      className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <label htmlFor="clipboard-content" className="block text-sm font-medium text-gray-700">
            Add to Shared Clipboard
          </label>
          <button
            onClick={handlePasteFromClipboard}
            className="text-xs flex items-center gap-1 text-primary-600 hover:text-primary-700 transition-colors"
            disabled={!isConnected || isSubmitting}
          >
            <SafeIcon icon={FiClipboard} className="w-3 h-3" />
            Paste from clipboard
          </button>
        </div>
        <textarea
          ref={textareaRef}
          id="clipboard-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type or paste content here... (Ctrl+Enter to send)"
          className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
          style={{ minHeight: '80px', maxHeight: '200px' }}
          disabled={!isConnected || isSubmitting}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Ctrl</kbd> +{' '}
          <kbd className="px-2 py-1 bg-gray-100 rounded text-xs ml-1">Enter</kbd> to send
        </div>
        <motion.button
          onClick={handleSubmit}
          disabled={!content.trim() || !isConnected || isSubmitting}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isSubmitting ? (
            <>
              <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <SafeIcon icon={FiSend} className="w-4 h-4" />
              Add to Clipboard
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ClipboardInput;